/**
 * Language Context for managing i18n state across the application
 */
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import {
  SUPPORTED_LANGUAGES,
  DEFAULT_LANGUAGE,
  getStoredLanguage,
  setStoredLanguage,
  loadTranslations,
  translate as translateFn,
  formatCurrency as formatCurrencyFn,
  formatDate as formatDateFn,
  formatNumber as formatNumberFn,
} from '../i18n';

const LanguageContext = createContext();

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(getStoredLanguage());
  const [translations, setTranslations] = useState({});
  const [loading, setLoading] = useState(true);

  // Load translations when language changes
  useEffect(() => {
    const loadLanguageTranslations = async () => {
      setLoading(true);
      try {
        const trans = await loadTranslations(language);
        setTranslations(trans);
      } catch (error) {
        console.error('Failed to load translations:', error);
      } finally {
        setLoading(false);
      }
    };

    loadLanguageTranslations();
  }, [language]);

  // Change language
  const changeLanguage = useCallback((newLang) => {
    if (SUPPORTED_LANGUAGES[newLang]) {
      setLanguage(newLang);
      setStoredLanguage(newLang);
    }
  }, []);

  // Translate function
  const t = useCallback((key, variables = {}) => {
    return translateFn(key, variables, translations);
  }, [translations]);

  // Format currency
  const formatCurrency = useCallback((amount, currency) => {
    return formatCurrencyFn(amount, currency, language);
  }, [language]);

  // Format date
  const formatDate = useCallback((date, format = 'short') => {
    return formatDateFn(date, language, format);
  }, [language]);

  // Format number
  const formatNumber = useCallback((number) => {
    return formatNumberFn(number, language);
  }, [language]);

  // Check if current language is RTL
  const isRTL = SUPPORTED_LANGUAGES[language]?.rtl || false;

  // Get template-specific label (e.g., "Lender", "Client", etc.)
  const getTemplateLabel = useCallback((templateType, labelType) => {
    // labelType can be 'principal' or 'secondary'
    const key = `templates.party_labels.${templateType.toLowerCase()}.${labelType}`;
    return t(key);
  }, [t]);

  // Get template-specific field label
  const getTemplateFieldLabel = useCallback((templateType, fieldType) => {
    const key = `templates.field_labels.${templateType.toLowerCase()}.${fieldType}`;
    return t(key);
  }, [t]);

  // Get template name
  const getTemplateName = useCallback((templateType) => {
    const key = `templates.${templateType.toLowerCase()}`;
    return t(key);
  }, [t]);

  // Get template description
  const getTemplateDescription = useCallback((templateType) => {
    const key = `templates.template_description.${templateType.toLowerCase()}`;
    return t(key);
  }, [t]);

  // Get interest frequency label
  const getFrequencyLabel = useCallback((frequency) => {
    const key = `interest.frequency_${frequency.toLowerCase()}`;
    return t(key);
  }, [t]);

  // Get status label
  const getStatusLabel = useCallback((status) => {
    const key = `loan.status.${status.toLowerCase()}`;
    return t(key);
  }, [t]);

  const value = {
    language,
    changeLanguage,
    t,
    formatCurrency,
    formatDate,
    formatNumber,
    isRTL,
    loading,
    supportedLanguages: SUPPORTED_LANGUAGES,
    // Template-specific helpers
    getTemplateLabel,
    getTemplateFieldLabel,
    getTemplateName,
    getTemplateDescription,
    getFrequencyLabel,
    getStatusLabel,
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

export default LanguageContext;
